import {
  Checkbox,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  WithStyles
} from '@material-ui/core';
import { createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { InjectedFormikProps, withFormik } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import MyButton from '../../../components/UI/MyButton/MyButton';
import { IFeed, IScreen } from '../../../models/states';
import { ScreenType } from '../../../utilities/utilities';

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    textField: {
      margin: '0',
      marginBottom: '1rem',
      width: '100%'
    },
    paper: {
      width: 600
    },
    customerWrapper: {
      backgroundColor: 'rgba(255,255,255,0.2)'
    },
    checkboxRoot: {
      '&$checked': theme.checkbox.yellow
    },
    checked: {},
    wrapper: {
      '&:focus': {
        outline: 'none'
      }
    }
  });

interface IFormValues {
  name: string;
  type: string;
  customers: { [key: string]: IFeed[] }; // multiple select field
  timezone: string;
}

interface IFormProps extends WithStyles<typeof styles> {
  screen: IScreen | undefined;
  onSubmit: (values: any) => void;
  customerToFeedMap: { [key: string]: IFeed[] };
  showCustomers: boolean;
  onShowCustomers: (e: any) => void;
  onCloseCustomers: (e: any) => void;
  customerAnchor: any;
  onToggleFeeds: (customer: string, feed: IFeed) => void;
  timezones: string[];
}

const InnerForm: React.SFC<
  InjectedFormikProps<IFormProps, IFormValues>
> = props => {
  const renderFeedSelect = () => {
    return (
      <Menu
        open={props.showCustomers}
        anchorEl={props.customerAnchor}
        onClose={props.onCloseCustomers}
        classes={{ paper: props.classes.paper }}
      >
        {Object.keys(props.customerToFeedMap).map((customerName: string) => {
          return (
            <div key={customerName} className={props.classes.wrapper}>
              <MenuItem
                className={props.classes.customerWrapper}
                key={customerName}
              >
                {customerName}
                {/* {open ? <ExpandLess /> : <ExpandMore />} */}
              </MenuItem>
              {props.customerToFeedMap[customerName].map(feed => {
                return (
                  <MenuItem
                    disableTouchRipple
                    disableRipple
                    onClick={() => props.onToggleFeeds(customerName, feed)}
                    key={feed.code + customerName}
                  >
                    <Checkbox
                      key={feed.code + customerName}
                      checked={feed.isChecked}
                      tabIndex={-1}
                      disableRipple
                      classes={{
                        root: props.classes.checkboxRoot,
                        checked: props.classes.checked
                      }}
                    />
                    {feed.name}
                  </MenuItem>
                );
              })}
            </div>
          );
        })}
      </Menu>
    );
  };
  const timezones = [];
  timezones.push(
    <MenuItem key="none" value="">
      None
    </MenuItem>,
    ...props.timezones.map((t: string, index: number) => (
      <MenuItem key={index} value={t}>
        {t}
      </MenuItem>
    ))
  );

  return (
    <div>
      <form onSubmit={props.handleSubmit}>
        <TextField
          id="name"
          label="Screen Name"
          onChange={props.handleChange}
          margin="normal"
          value={props.values.name}
          className={props.classes.textField}
          error={Boolean(props.errors.name && props.touched.name)}
          autoComplete={'off'}
          helperText={
            props.errors.name && props.touched.name && String(props.errors.name)
          }
          InputLabelProps={{
            shrink: true
          }}
        />
        <FormControl className={props.classes.textField}>
          <InputLabel htmlFor="select-multiple" shrink={true}>
            Screen Type
          </InputLabel>
          <Select
            id="type"
            value={props.values.type}
            className={props.classes.textField}
            onChange={props.handleChange}
            margin="none"
            name="type"
          >
            <MenuItem key={'ingest'} value={ScreenType.INGEST}>
              Ingest
            </MenuItem>
            <MenuItem key={'playlist'} value={ScreenType.PLAYLIST}>
              Playlist
            </MenuItem>
            <MenuItem key={'playout'} value={ScreenType.PLAYOUT}>
              Playout
            </MenuItem>
          </Select>
        </FormControl>
        <FormControl className={props.classes.textField}>
          <TextField
            id="customers"
            label="Feeds"
            onClick={props.onShowCustomers}
            margin="normal"
            autoComplete={'off'}
            value={Object.keys(props.customerToFeedMap)
              .filter(customer => {
                if (
                  props.customerToFeedMap[customer].filter(feed => {
                    if (feed.isChecked) {
                      return true;
                    } else {
                      return false;
                    }
                  }).length > 0
                ) {
                  return true;
                } else {
                  return false;
                }
              })
              .map(customer => {
                return `${props.customerToFeedMap[customer]
                  .filter(feed => feed.isChecked)
                  .map(feed => feed.name)
                  .join(' , ')}`;
              })}
            className={props.classes.textField}
            InputLabelProps={{
              shrink: true
            }}
          />
          {renderFeedSelect()}
        </FormControl>
        <FormControl className={props.classes.textField}>
          <InputLabel htmlFor="select-multiple" shrink={true}>
            Timezone
          </InputLabel>
          <Select
            id="timezone"
            value={props.values.timezone}
            disabled={props.values.type !== ScreenType.PLAYOUT}
            className={props.classes.textField}
            onChange={props.handleChange}
            margin="none"
            name="timezone"
          >
            {timezones}
          </Select>
        </FormControl>
        <MyButton
          btnType={'yellow'}
          className={props.classes.textField}
          type="submit"
          disabled={props.isSubmitting}
        >
          Save
        </MyButton>
      </form>
    </div>
  );
};

const ScreenForm = withFormik<IFormProps, IFormValues>({
  mapPropsToValues: props => ({
    screenID: props.screen ? props.screen.screenID : '',
    name: props.screen ? props.screen.name : '',
    type: props.screen ? props.screen.type : ScreenType.INGEST,
    customers: props.customerToFeedMap,
    timezone: props.screen && props.screen.timezone ? props.screen.timezone : ''
  }),
  validationSchema: Yup.object().shape({
    name: Yup.string().required('*Required')
    // customers: Yup.string().required("*Required")
  }),
  handleSubmit: (values, { props }) => {
    props.onSubmit(values);
  }
})(InnerForm);

export default withStyles(styles)(ScreenForm);
